import { css } from 'emotion';

import getFontStyles from '../../../../lib/styles/getFontStyles';
import getLineClampStyles from '../../../../lib/styles/getLineClampStyles';
import {
  LINES_ALLOWED,
  LINE_HEIGHT,
} from '../../../../lib/styles/dynamicStyleConstants';
import { ISettings } from '../../../../lib/settings/types';

import {
  ISettingsColors,
  ISettingsFonts,
  MainHeaderStyle,
  MainHeaderTextAlignment,
} from '@wix/podcasts-common/dist/settings';

import { TPADeviceType } from '@wix/podcasts-common/dist/tpa';

interface IGetStylesArgs {
  settings: ISettings;
  colors: ISettingsColors['main'];
  fonts: ISettingsFonts['main'];
  hasImage: boolean;
  deviceType: TPADeviceType;
}

export default function getStyles({
  colors,
  fonts,
  settings,
  hasImage,
  deviceType,
}: IGetStylesArgs) {
  const { headerStyle, headerTextAlignment } = settings.main;

  const isImageShown = headerStyle !== MainHeaderStyle.MINIMAL && hasImage;

  const isTextCentered =
    !isImageShown && headerTextAlignment === MainHeaderTextAlignment.CENTER;

  return {
    headerWrapper: css({
      backgroundColor: colors.headerBackgroundColor,
    }),
    headerTitle: css({
      color: colors.headerTitleColor,
      ...getFontStyles(fonts.headerTitleFont, {
        lineHeight: LINE_HEIGHT.TITLE,
      }),
      ...{
        [TPADeviceType.MOBILE]: {
          fontSize: 22,
        },
      }[deviceType],
      ...getLineClampStyles(
        deviceType === TPADeviceType.MOBILE
          ? { size: 22 }
          : fonts.headerTitleFont,
        LINE_HEIGHT.TITLE,
        LINES_ALLOWED.MAIN_HEADER_TITLE,
      ),
    }),
    headerHost: css({
      color: colors.headerSubtitleColor,
      ...getFontStyles(fonts.headerSubtitleFont),
      ...{
        [TPADeviceType.MOBILE]: {
          fontSize: 14,
        },
      }[deviceType],
      ...getLineClampStyles(
        deviceType === TPADeviceType.MOBILE
          ? { size: 14 }
          : fonts.headerSubtitleFont,
        LINE_HEIGHT.DEFAULT,
        LINES_ALLOWED.MAIN_HEADER_SUBTITLE,
      ),
    }),
    headerDescriptionContainer: css({
      alignContent: isTextCentered ? 'center' : 'flex-start',
    }),
    headerDescription: css({
      color: colors.headerDescriptionColor,
      ...getFontStyles(fonts.headerDescriptionFont),
      ...{
        [TPADeviceType.MOBILE]: {
          fontSize: 14,
        },
      }[deviceType],
      '& a': { color: colors.headerDescriptionColor },
      '& a:hover': { color: colors.headerButtonColor },
      '& a:active': { color: colors.headerButtonColor },
    }),
    headerDescriptionShort: css({
      ...getLineClampStyles(
        deviceType === TPADeviceType.MOBILE
          ? { size: 14 }
          : fonts.headerDescriptionFont,
        LINE_HEIGHT.DEFAULT,
        LINES_ALLOWED.MAIN_HEADER_DESCRIPTION,
      ),
      '& a': { color: colors.headerDescriptionColor },
      '& a:hover': { color: colors.headerButtonColor },
      '& a:active': { color: colors.headerButtonColor },
    }),
    headerReadMore: css({
      color: colors.headerDescriptionColor,
      ...getFontStyles(fonts.headerDescriptionFont),
      ...{
        [TPADeviceType.DESKTOP]: {
          fontSize: 16,
        },
        [TPADeviceType.MOBILE]: {
          fontSize: 14,
        },
      }[deviceType],
      '&:hover': {
        color: colors.headerButtonColor,
      },
      '&:active': {
        color: colors.headerButtonColor,
      },
      ...{
        [TPADeviceType.DESKTOP]: {
          textAlign: isTextCentered ? 'center' : 'left',
        },
      }[deviceType],
    }),
  };
}
